import { ChangeDetectionStrategy, Component, TemplateRef, ViewEncapsulation, computed, effect, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize, ButtonOverlayComponent, ButtonOverlayContentDirective } from '@workbuddy/ui/v2/components/button';
import { LuxonCalendarComponent } from '@workbuddy/ui/fields/luxon.calendar/component';
import type { BoardHeaderNavigation } from '../../state/features/with.header.navigation';

/** The header renders navigation; the mounted display owns its meaning and date arithmetic. */
@Component({
  selector: 'board-header-navigation',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonComponent, ButtonOverlayComponent, ButtonOverlayContentDirective, ReactiveFormsModule, LuxonCalendarComponent, NgTemplateOutlet],
  styleUrls: ['./board.header.navigation.component.less'],
  host: { role: 'group', 'aria-label': 'Calendar navigation' },
  template: `
    @let navigation = $navigation();
    @if (navigation.controlsVisible()) {
      <div class="header-navigation-actions">
        <wb-button [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Secondary" iconLeft="fa-regular fa-chevron-left" tooltip="Previous period" (onClick)="navigation.previous()" />
        <wb-button [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Secondary" label="Today" (onClick)="navigation.today()" />
        <wb-button [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Secondary" iconLeft="fa-regular fa-chevron-right" tooltip="Next period" (onClick)="navigation.next()" />
      </div>
      <div class="header-navigation-period">
        <wb-button-overlay class="header-navigation-date" aria-live="polite" aria-atomic="true" [label]="navigation.title()" tooltip="Go to date" displayStyle="Tertiary" menuAlignment="Left" overlayClass="board-header-date-picker" [overlayViewportMargin]="12" #dateOverlay>
        <ng-template buttonOverlayContent>
          @if (pickerContent(); as content) {
            <ng-container [ngTemplateOutlet]="content" />
          } @else {
            <luxon-calendar #datePicker [zone]="$timezone()" [formControl]="dateControl" [defaultDate]="dateControl.value" [firstDayOfWeek]="navigation.firstDayOfWeek ?? null" [inline]="true" [showTime]="false" [showButtonBar]="false" [selectOtherMonths]="true" (onSelect)="onDateSelected(datePicker.value, dateOverlay)" />
          }
        </ng-template>
        </wb-button-overlay>
      </div>
    } @else {
      <span class="header-navigation-title" aria-live="polite" aria-atomic="true">{{ navigation.title() }}</span>
    }
  `,
})
export class BoardHeaderNavigationComponent {
  /** A bounded table may provide period/range choices inside the same calendar navigation popover. */
  public readonly pickerContent = input<TemplateRef<unknown> | null>(null);
  public readonly $navigation = input.required<BoardHeaderNavigation>({ alias: 'navigation' });
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly dateControl = new FormControl<DateTime | null>(null);
  protected readonly $timezone = computed(() => this.$navigation().timezone?.() ?? 'local');

  public constructor() {
    effect(() => {
      const date = DateTime.fromISO(this.$navigation().date(), { zone: this.$timezone() });
      this.dateControl.setValue(date.isValid ? date : null);
    });
  }

  protected onDateSelected(date: DateTime, overlay: ButtonOverlayComponent): void {
    if (!date?.isValid) return;
    this.$navigation().goTo(date.toISODate()!);
    overlay.hideMenu();
  }
}
